<template>
  <div class="procurement-settle">
    <div class="settle-header">
      <h2>{{ pageTitle }}</h2>
      <p>{{ pageDescription }}</p>
    </div>

    <el-alert
      v-if="applicationStatus !== null"
      :title="statusText"
      :type="statusAlertType"
      :closable="false"
      show-icon
      class="status-alert"
    />

    <div class="settle-form">
      <el-form
        ref="settleForm"
        :model="formData"
        :rules="rules"
        label-width="120px"
        label-position="left"
      >
        <!-- 企业名称 -->
        <el-form-item label="企业名称" prop="companyName">
          <el-input
            v-model="formData.companyName"
            placeholder="请输入企业名称"
            clearable
            :disabled="isReadonly"
          />
        </el-form-item>

        <!-- 企业地址 -->
        <el-form-item label="企业地址" prop="companyAddress">
          <el-input
            v-model="formData.companyAddress"
            placeholder="请输入企业地址"
            clearable
            :disabled="isReadonly"
          />
        </el-form-item>

        <!-- 企业电话 -->
        <el-form-item label="企业电话" prop="companyPhone">
          <el-input
            v-model="formData.companyPhone"
            placeholder="请输入企业电话"
            clearable
            :disabled="isReadonly"
          />
        </el-form-item>

        <!-- 产品领域 -->
        <el-form-item label="产品领域" prop="productFieldId">
          <el-select
            v-model="formData.productFieldId"
            placeholder="请选择产品领域"
            clearable
            @change="handleProductFieldChange"
            :disabled="isReadonly"
          >
            <el-option
              v-for="item in productFields"
              :key="item.category_id"
              :label="item.category_name"
              :value="item.category_id"
            />
          </el-select>
        </el-form-item>

        <!-- 企业联系人 -->
        <el-form-item label="企业联系人" prop="contactName">
          <el-input
            v-model="formData.contactName"
            placeholder="请输入企业联系人"
            clearable
            :disabled="isReadonly"
          />
        </el-form-item>

        <!-- 联系人电话 -->
        <el-form-item label="联系人电话" prop="contactPhone">
          <el-input
            v-model="formData.contactPhone"
            placeholder="请输入联系人电话"
            clearable
            :disabled="isReadonly"
          />
        </el-form-item>

        <!-- 年度采购额 -->
        <el-form-item label="年度采购额" prop="serviceConfig">
          <el-radio-group v-model="formData.serviceConfig" :disabled="isReadonly">
            <el-radio label="1">1万-10万</el-radio>
            <el-radio label="2">10万-50万</el-radio>
            <el-radio label="3">50万-100万</el-radio>
            <el-radio label="4">100万以上</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 企业资质上传 -->
        <el-form-item label="企业资质" prop="qualificationImages">
          <div class="upload-container">
            <el-upload
              ref="upload"
              :action="uploadUrl"
              :headers="uploadHeaders"
              :data="uploadData"
              :file-list="fileList"
              :before-upload="beforeUpload"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              :on-remove="handleRemove"
              list-type="picture-card"
              :limit="1"
              accept="image/*"
              :disabled="isReadonly"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
            <div class="upload-tip">
              <p>支持上传JPG、PNG格式图片，最多9张</p>
            </div>
          </div>
        </el-form-item>

        <!-- 提交按钮 -->
        <el-form-item v-if="canSubmit">
          <div class="submit-container">
            <el-button type="primary" @click="submitForm" :loading="submitting">
              {{ submitting ? '提交中...' : submitButtonText }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { getRegionList } from '@/api/address'
import { getProductDomain, submitProcurementApply, getProcurementApplyInfo, getProcurementApplyStatus } from '@/api/procurement'
import Config from '@/utils/config'

export default {
  name: 'ProcurementSettle',
  props: {
    inMemberCenter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 表单数据
      formData: {
        companyName: '', // 企业名称
        companyAddress: '', // 企业地址
        companyPhone: '', // 企业电话
        productFieldId: '', // 产品领域ID
        contactName: '', // 企业联系人
        contactPhone: '', // 联系人电话
        serviceConfig: '', // 年度采购额
        qualificationImages: [] // 企业资质图片
      },
      
      // 表单验证规则
      rules: {
        companyName: [
          { required: true, message: '请输入企业名称', trigger: 'blur' }
        ],
        companyAddress: [
          { required: true, message: '请输入企业地址', trigger: 'blur' }
        ],
        companyPhone: [
          { required: true, message: '请输入企业电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/, message: '请输入正确的电话号码', trigger: 'blur' }
        ],
        productFieldId: [
          { required: true, message: '请选择产品领域', trigger: 'change' }
        ],
        contactName: [
          { required: true, message: '请输入企业联系人', trigger: 'blur' }
        ],
        contactPhone: [
          { required: true, message: '请输入联系人电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        serviceConfig: [
          { required: true, message: '请选择年度采购额', trigger: 'change' }
        ],
        qualificationImages: [
          { required: true, message: '请上传企业资质', trigger: 'change' }
        ]
      },
      
      // 产品领域选项
      productFields: [],
      
      // 上传相关
      uploadUrl: Config.baseUrl + '/api/upload/shopimg',
      uploadHeaders: {},
      uploadData: {
        token: this.$store.getters.token || '',
        path: 'shopimg'
      },
      fileList: [],
      submitting: false,
      applicationStatus: null,
      applicationLoading: false,
      
      // 页面类型：apply-申请，reapply-重新申请
      pageType: 'apply'
    }
  },
  
  created() {
    this.getProductDomains()

    if (this.$route.query.type === 'reapply') {
      this.pageType = 'reapply'
    }

    if (this.inMemberCenter) {
      this.loadApplicationInfo()
    } else if (this.pageType === 'reapply') {
      this.getProcurementInfo()
    }
  },

  computed: {
    isReadonly() {
      return this.inMemberCenter && this.applicationStatus !== null && this.applicationStatus !== -1
    },
    canSubmit() {
      return !this.isReadonly
    },
    statusText() {
      const statusMap = {
        '-1': '审核未通过，请修改资料后重新提交',
        '1': '审核中，提交资料暂不可修改',
        '2': '审核通过'
      }
      return statusMap[this.applicationStatus] || ''
    },
    statusAlertType() {
      if (this.applicationStatus === 2) return 'success'
      if (this.applicationStatus === -1) return 'error'
      return 'warning'
    },
    pageTitle() {
      if (this.isReadonly) return '采购商入驻信息'
      return this.pageType === 'reapply' ? '采购商重新申请' : '采购商入驻申请'
    },
    pageDescription() {
      if (this.applicationStatus === 1) return '您的申请正在审核，请耐心等待审核结果'
      if (this.applicationStatus === 2) return '您的采购商入驻申请已审核通过'
      if (this.applicationStatus === -1) return '请修改以下信息后重新提交申请'
      return '请填写以下信息完成采购商入驻申请'
    },
    submitButtonText() {
      return this.pageType === 'reapply' ? '重新提交' : '确认提交'
    }
  },
  
  methods: {
    async loadApplicationInfo() {
      this.applicationLoading = true
      try {
        const statusRes = await getProcurementApplyStatus()
        if (statusRes.code !== 0 || !statusRes.data || !statusRes.data.has_apply) return

        this.applicationStatus = Number(statusRes.data.status)
        if (this.applicationStatus === -1) this.pageType = 'reapply'
        await this.getProcurementInfo(false)
      } catch (error) {
        console.error('获取采购商申请状态失败:', error)
      } finally {
        this.applicationLoading = false
      }
    },

    // 获取产品领域数据
    async getProductDomains() {
      try {
        const res = await getProductDomain()
        if (res.code === 0 && res.data) {
          this.productFields = res.data.map(item => ({
            category_id: item.category_id,
            category_name: item.category_name
          }))
        }
      } catch (error) {
        console.error('获取产品领域失败:', error)
        this.$message.error('获取产品领域失败')
      }
    },
    
    // 获取采购商入驻信息（重新申请时使用）
    async getProcurementInfo(showError = true) {
      try {
        const res = await getProcurementApplyInfo()
        if (res.code === 0 && res.data) {
          const data = res.data
          
          // 填充表单数据
          this.formData.companyName = data.company_name || ''
          this.formData.companyAddress = data.company_address || ''
          this.formData.companyPhone = data.company_phone || ''
          this.formData.contactName = data.contact_name || ''
          this.formData.contactPhone = data.contact_phone || ''
          this.formData.productFieldId = data.category_id || ''
          this.formData.serviceConfig = data.service_config ? String(data.service_config) : ''
          
          // 处理企业资质图片
          if (data.qualification_images) {
            const images = Array.isArray(data.qualification_images) ? data.qualification_images : String(data.qualification_images).split(',').filter(item => item.trim())
            this.formData.qualificationImages = images
            this.fileList = images.map((url, index) => ({
              name: `image_${index}`,
              url: this.$img(url),
              response: { pic_path: url }
            }))
          }
        }
      } catch (error) {
        console.error('获取入驻信息失败:', error)
        if (showError) this.$message.error('获取入驻信息失败')
      }
    },
    
    // 产品领域选择
    handleProductFieldChange(value) {
      this.formData.productFieldId = value
    },
    
    // 上传前处理
    beforeUpload(file) {
      // 更新token
      this.uploadData.token = this.$store.getters.token || ''
      
      const isImage = file.type.indexOf('image/') === 0
      const isLt2M = file.size / 1024 / 1024 < 2
      
      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!')
        return false
      }
      return true
    },
    
    // 上传成功处理
    handleUploadSuccess(response, file, fileList) {
      if (response.code === 1 || response.code === 0 || response.code === 10069) {
        this.formData.qualificationImages.push(response.data.pic_path)
        this.fileList = fileList
        this.$message({
          message: '上传成功',
          type: 'success'
        })
      } else {
        this.$message.error(response.message || response.msg || '上传失败')
        // 移除上传失败的文件
        this.$refs.upload.handleRemove(file)
      }
    },
    
    // 上传失败处理
    handleUploadError(err, file, fileList) {
      console.error('上传失败:', err)
      this.$message.error('上传失败，请重试')
    },
    
    // 移除文件
    handleRemove(file, fileList) {
      if (file.response && file.response.pic_path) {
        const index = this.formData.qualificationImages.indexOf(file.response.pic_path)
        if (index > -1) {
          this.formData.qualificationImages.splice(index, 1)
        }
      }
      this.fileList = fileList
    },
    
    // 提交表单
    submitForm() {
      this.$refs.settleForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        if (this.formData.qualificationImages.length === 0) {
          this.$message.error('请上传企业资质')
          return false
        }
        
        // 显示确认弹窗
        try {
          await this.$confirm('信息已填写完毕，确定提交？', '确认提交', {
            confirmButtonText: '确认',
            cancelButtonText: '取消',
            type: 'warning'
          })
          
          this.doSubmit()
        } catch (error) {
          // 用户取消提交
        }
      })
    },
    
    // 执行提交
    async doSubmit() {
      this.submitting = true
      
      try {
        const submitData = {
          company_name: this.formData.companyName,
          company_address: this.formData.companyAddress,
          company_phone: this.formData.companyPhone,
          category_id: this.formData.productFieldId,
          contact_name: this.formData.contactName,
          contact_phone: this.formData.contactPhone,
          service_config: this.formData.serviceConfig,
          qualification_images: this.formData.qualificationImages.join(',')
        }
        
        const res = await submitProcurementApply(submitData)
        
        if (res.code === 0) {
          this.$message.success('提交成功，请等待审核')
          
          // 更新用户信息
          this.updateUserInfo()
          
          // 提交成功后进入会员中心回显申请状态和资料
          setTimeout(() => {
            this.$router.push('/member/settle?tab=procurement')
          }, 1500)
        } else {
          // 优先使用返回的data内容，其次使用message，最后使用默认提示
          const errorMsg = res.data || res.message || '提交失败'
          this.$message.error(errorMsg)
        }
      } catch (error) {
        console.error('提交失败:', error)
        // 检查是否是接口返回的错误信息
        if (error && error.data) {
          const errorMsg = error.data || '提交失败'
          this.$message.error(errorMsg)
        } else {
          this.$message.error('网络错误，请重试')
        }
      } finally {
        this.submitting = false
      }
    },
    
    // 更新用户信息
    async updateUserInfo() {
      try {
        await this.$store.dispatch('member/member_detail', { refresh: 1 })
      } catch (error) {
        console.error('更新用户信息失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.procurement-settle {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;

  .status-alert {
    max-width: 800px;
    margin: 0 auto 20px;
  }
}

.settle-header {
  text-align: center;
  margin-bottom: 40px;
  
  h2 {
    font-size: 28px;
    color: #333;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 14px;
    color: #666;
  }
}

.settle-form {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  
  .el-form-item {
    margin-bottom: 30px;
  }
  
  .el-input,
  .el-select {
    width: 100%;
  }
  
  .el-radio-group {
    .el-radio {
      margin-right: 30px;
      margin-bottom: 10px;
    }
  }
}

.upload-container {
  .upload-tip {
    margin-top: 10px;
    
    p {
      font-size: 12px;
      color: #999;
      margin: 0;
    }
  }
}

.submit-container {
  text-align: center;
  margin-top: 40px;
  
  .el-button {
    width: 200px;
    height: 44px;
    font-size: 16px;
  }
}

// 响应式适配
@media screen and (max-width: 768px) {
  .procurement-settle {
    padding: 10px;
  }
  
  .settle-form {
    padding: 20px;
  }
  
  .el-radio-group {
    .el-radio {
      display: block;
      margin-right: 0;
      margin-bottom: 15px;
    }
  }
}
</style>
